import React, { useState, useCallback, useEffect } from 'react';
import { BarChart2, Save, RefreshCw, Download, Clipboard, Megaphone } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import CampaignBasicsSection from './CampaignBasicsSection';
import PlatformsSection from './PlatformsSection';
import TargetAudienceSection from './TargetAudienceSection';
import GoalsSection from './GoalsSection';
import AdvancedOptionsSection from './AdvancedOptionsSection';

// Импорт утилитарных функций
import { 
  isPositiveNumber, 
  isInteger,
  isNonNegativeNumber 
} from '../../../../../utils/validation';

const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  platformTypes,
  saveCampaign 
}) => {
  // Состояние для имени сохраняемой кампании
  const [saveCampaignName, setSaveCampaignName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Обработчик изменений в основных полях формы
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, [setFormData]);

  // Обработчик изменений в платформах
  const handlePlatformChange = useCallback((platformId, field, value) => {
    setFormData(prev => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platformId]: {
          ...prev.platforms[platformId],
          [field]: value
        }
      }
    }));
  }, [setFormData]);

  // Обработчик изменений в целевой аудитории
  const handleTargetAudienceChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      targetAudience: {
        ...prev.targetAudience,
        [field]: value
      }
    }));
  }, [setFormData]);

  // Обработчик изменений в целях кампании
  const handleGoalsChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      goals: {
        ...prev.goals,
        [field]: value
      }
    }));
  }, [setFormData]);

  // Обработчик изменений в расширенных опциях
  const handleAdvancedChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        [field]: value
      }
    }));
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Валидация основных полей
    if (!formData.campaignName || formData.campaignName.trim() === '') {
      newErrors.campaignName = 'Введите название кампании';
    }

    const totalBudget = parseFloatOrDefault(formData.totalBudget);
    if (isNaN(totalBudget) || totalBudget <= 0) {
      newErrors.totalBudget = 'Введите корректный бюджет';
    }

    const campaignDuration = parseIntOrDefault(formData.campaignDuration);
    if (isNaN(campaignDuration) || campaignDuration <= 0) {
      newErrors.campaignDuration = 'Введите корректную длительность';
    }

    // Валидация платформ
    const enabledPlatforms = Object.values(formData.platforms)
      .filter(platform => platform.enabled);

    if (enabledPlatforms.length === 0) {
      newErrors.platforms = 'Выберите хотя бы одну платформу';
    }

    const totalBudgetPercent = enabledPlatforms
      .reduce((sum, platform) => sum + parseFloatOrDefault(platform.budget, 0), 0);

    if (Math.abs(totalBudgetPercent - 100) > 0.1) {
      newErrors.platformBudgets = 'Сумма долей бюджета должна быть равна 100%';
    }

    // Валидация целевой аудитории
    const audienceSize = parseIntOrDefault(formData.targetAudience.size);
    if (isNaN(audienceSize) || audienceSize <= 0) {
      newErrors.audience_size = 'Введите корректный размер аудитории';
    }

    const reachPercent = parseFloatOrDefault(formData.targetAudience.reachPercent);
    if (isNaN(reachPercent) || reachPercent <= 0 || reachPercent > 100) {
      newErrors.audience_reachPercent = 'Введите корректный процент охвата (0-100)';
    }

    // Валидация целей
    const totalGoalsWeight = 
      parseFloatOrDefault(formData.goals.awarenessWeight, 0) +
      parseFloatOrDefault(formData.goals.engagementWeight, 0) +
      parseFloatOrDefault(formData.goals.conversionWeight, 0);

    if (Math.abs(totalGoalsWeight - 100) > 0.1) {
      newErrors.goals_total = 'Сумма весов целей должна быть равна 100%';
    }

    // Валидация расширенных опций
    if (formData.advanced.includeProductionCosts) {
      const productionCost = parseFloatOrDefault(formData.advanced.productionCost);
      if (isNaN(productionCost) || productionCost < 0) {
        newErrors.advanced_productionCost = 'Введите корректную стоимость производства';
      }
    }

    if (formData.advanced.includeAgencyFee) {
      const agencyFeePercent = parseFloatOrDefault(formData.advanced.agencyFeePercent);
      if (isNaN(agencyFeePercent) || agencyFeePercent < 0 || agencyFeePercent > 100) {
        newErrors.advanced_agencyFeePercent = 'Введите корректный процент комиссии (0-100)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors, parseFloatOrDefault, parseIntOrDefault]);

// Расчет кампании
const calculateCampaign = useCallback(() => {
  // Сначала проводим валидацию
  if (!validateForm()) {
    return;
  }

  // Функции для парсинга чисел (определены здесь, чтобы быть доступными в этой области видимости)
  const parseFloatOrDefault = (val, defaultVal = 0) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  const parseIntOrDefault = (val, defaultVal = 0) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Начинаем калькуляцию с индикацией загрузки
  setIsCalculating(true);
  
  // Имитация асинхронного расчета
  setTimeout(() => {
    try {
      // Получаем общий бюджет и длительность
      const totalBudget = parseFloatOrDefault(formData.totalBudget, 0);
      const campaignDuration = parseIntOrDefault(formData.campaignDuration, 1);
      
      // Пример структуры результатов
      const calculatedResults = {
        campaign: {
          name: formData.campaignName,
          totalBudget: totalBudget,
          campaignDuration: campaignDuration,
          costPerDay: totalBudget / campaignDuration
        },
        platforms: Object.fromEntries(
          Object.entries(formData.platforms)
            .filter(([, platform]) => platform.enabled)
            .map(([platformId, platform]) => {
              // Рассчитываем бюджет для каждой платформы
              const platformBudgetPercent = parseFloatOrDefault(platform.budget, 0);
              const platformBudget = totalBudget * (platformBudgetPercent / 100);
              const cpc = parseFloatOrDefault(platform.cpc, 0);
              const ctr = parseFloatOrDefault(platform.ctr, 0) / 100;
              
              // Рассчитываем показы и клики
              const impressions = Math.round(platformBudget / cpc * 100);  // Примерная формула
              const clicks = Math.round(impressions * ctr);
              const conversionRate = parseFloatOrDefault(platform.conversionRate, 0) / 100;
              const conversions = Math.round(clicks * conversionRate);
              
              // Рассчитываем стоимость конверсии
              const cpa = conversions > 0 ? platformBudget / conversions : 0;
              
              // Рассчитываем ROI (примерная формула)
              const estimatedRevenue = conversions * (cpa * 1.5);  // Примерная формула для дохода
              const roi = platformBudget > 0 ? ((estimatedRevenue - platformBudget) / platformBudget) * 100 : 0;
              
              return [platformId, {
                budget: platformBudget,
                budgetPercent: platformBudgetPercent,
                cpc: cpc,
                impressions: impressions,
                clicks: clicks,
                ctr: ctr,
                conversions: conversions,
                cpa: cpa,
                roi: roi
              }];
            })
        ),
        audience: {
          size: parseIntOrDefault(formData.targetAudience.size, 0),
          reachPercent: parseFloatOrDefault(formData.targetAudience.reachPercent, 0),
          estimatedReach: Math.round(
            parseIntOrDefault(formData.targetAudience.size, 0) * 
            parseFloatOrDefault(formData.targetAudience.reachPercent, 0) / 100
          )
        },
        performance: {
          totalImpressions: 0,
          totalClicks: 0,
          totalConversions: 0,
          averageCTR: 0,
          averageConversionRate: 0,
          totalROI: 0
        },
        effectiveness: {
          score: 0
        }
      };

      // Если нет платформ, устанавливаем значения по умолчанию
      if (Object.keys(calculatedResults.platforms).length === 0) {
        setResults(null);
        setIsCalculating(false);
        return;
      }

      // Агрегируем общие показатели
      const platforms = Object.values(calculatedResults.platforms);
      const totalImpressions = platforms.reduce((sum, platform) => sum + platform.impressions, 0);
      const totalClicks = platforms.reduce((sum, platform) => sum + platform.clicks, 0);
      const totalConversions = platforms.reduce((sum, platform) => sum + platform.conversions, 0);
      
      // Рассчитываем средние показатели
      const averageCTR = platforms.reduce((sum, platform) => sum + platform.ctr, 0) / platforms.length * 100;
      const averageConversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
      const totalROI = platforms.reduce((sum, platform) => sum + platform.roi, 0) / platforms.length;
      
      calculatedResults.performance = {
        totalImpressions,
        totalClicks,
        totalConversions,
        averageCTR,
        averageConversionRate,
        totalROI
      };
      
      // Рассчитываем оценку эффективности кампании (0-100)
      const ctcScore = Math.min(40, averageCTR * 2); // CTR влияет на 40% оценки
      const conversionScore = Math.min(30, averageConversionRate * 3); // Конверсия влияет на 30% оценки
      const roiScore = Math.min(30, totalROI / 2); // ROI влияет на 30% оценки
      
      calculatedResults.effectiveness.score = Math.round(ctcScore + conversionScore + roiScore);

      setResults(calculatedResults);
    } catch (error) {
      console.error("Ошибка при расчете кампании:", error);
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  }, 1500); // Имитация задержки расчета
}, [formData, validateForm, setIsCalculating, setResults]);

  // Обработчик сохранения кампании
  const handleSaveCampaign = () => {
    if (!results) return;
    setShowSaveDialog(true);
  };

  // Подтверждение сохранения кампании
  const confirmSaveCampaign = () => {
    if (!results) return;

    // Если имя не указано, используем имя по умолчанию
    const campaignName = saveCampaignName.trim() || 
      `Кампания с бюджетом ${formData.totalBudget}₽`;

    // Вызываем функцию сохранения из пропсов
    const savedCampaign = saveCampaign(campaignName);
    
    // Закрываем диалог и сбрасываем имя
    setShowSaveDialog(false);
    setSaveCampaignName('');
  };

  // Обработчик экспорта результатов
  const handleExportResults = () => {
    if (!results) return;

    // Создаем JSON для экспорта
    const exportData = {
      campaignName: formData.campaignName,
      totalBudget: formData.totalBudget,
      campaignDuration: formData.campaignDuration,
      platforms: Object.entries(formData.platforms)
        .filter(([, platform]) => platform.enabled)
        .map(([platformId, platform]) => ({
          name: platformId,
          budget: platform.budget,
          cpc: platform.cpc,
          ctr: platform.ctr,
          conversionRate: platform.conversionRate
        })),
      results: {
        totalImpressions: results.performance.totalImpressions,
        totalClicks: results.performance.totalClicks,
        totalConversions: results.performance.totalConversions,
        averageCTR: results.performance.averageCTR,
        effectiveness: results.effectiveness.score
      }
    };

    // Создаем Blob для скачивания
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Создаем ссылку для скачивания
    const link = document.createElement('a');
    link.href = url;
    link.download = `campaign_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик копирования результатов
  const handleCopyResults = () => {
    if (!results) return;

    // Создаем текстовое представление результатов
    const resultText = `Рекламная кампания: ${formData.campaignName}
Бюджет: ${formData.totalBudget} ₽
Длительность: ${formData.campaignDuration} дней

Общие показатели:
- Количество показов: ${results.performance.totalImpressions}
- Количество кликов: ${results.performance.totalClicks}
- Количество конверсий: ${results.performance.totalConversions}
- Средний CTR: ${(results.performance.averageCTR * 100).toFixed(2)}%
- Эффективность: ${results.effectiveness.score}/100`;

    // Копируем текст в буфер обмена
    navigator.clipboard.writeText(resultText)
      .then(() => {
        alert('Результаты кампании скопированы в буфер обмена');
      })
      .catch(err => {
        console.error('Ошибка копирования:', err);
        alert('Не удалось скопировать результаты');
      });
  };

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      calculateCampaign();
    }} className="space-y-6">
      <CampaignBasicsSection 
        formData={formData} 
        handleInputChange={handleInputChange} 
        errors={errors} 
      />

      <PlatformsSection 
        formData={formData} 
        handlePlatformChange={handlePlatformChange} 
        errors={errors} 
        platformTypes={platformTypes}
      />

      <TargetAudienceSection 
        formData={formData} 
        handleTargetAudienceChange={handleTargetAudienceChange} 
        errors={errors} 
      />

      <GoalsSection 
        formData={formData} 
        handleGoalsChange={handleGoalsChange} 
        errors={errors} 
      />

      <AdvancedOptionsSection 
        formData={formData} 
        handleAdvancedChange={handleAdvancedChange} 
        errors={errors} 
      />

      {/* Блок действий с кампанией */}
      <div className="flex flex-wrap gap-4 justify-between items-center">
        {/* Кнопка расчета */}
        <Button 
          type="submit" 
          variant="primary" 
          size="lg" 
          disabled={isCalculating}
          icon={isCalculating ? <RefreshCw className="animate-spin" /> : <BarChart2 />}
        >
          {isCalculating ? 'Расчет...' : 'Рассчитать кампанию'}
        </Button>

        {/* Кнопки для работы с результатами */}
        {results && (
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="secondary" 
              size="lg" 
              icon={<Save />}
              onClick={handleSaveCampaign}
            >
              Сохранить
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="lg" 
              icon={<Download />}
              onClick={handleExportResults}
            >
              Экспорт
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="lg" 
              icon={<Clipboard />}
              onClick={handleCopyResults}
            >
              Копировать
            </Button>
          </div>
        )}
      </div>

      {/* Диалог сохранения кампании */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
              Сохранение кампании
            </h3>
            <input 
              type="text"
              value={saveCampaignName}
              onChange={(e) => setSaveCampaignName(e.target.value)}
              placeholder="Название кампании"
              className="form-input w-full mb-4"
            />
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="secondary"
                onClick={() => setShowSaveDialog(false)}
              >
                Отмена
              </Button>
              <Button 
                type="button" 
                variant="primary"
                onClick={confirmSaveCampaign}
              >
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default CalculationForm;